// 加工指示グループ操作ダイアログの状態遷移と追加/編集/削除/複写の適用をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createKakousijiGroupDialogService(deps) {
    const {
        DeepCopy,
        DicVal,
        KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE,
        KAKOUSIJI_QUALITY_BASE_PANEL_TYPE,
        createKakousijiGroupRow,
        fnc,
        getKakousijiGridRowsByType,
        getKnownKakousijiGroupNos,
        initialPdtcDetail220Ref,
        isEmpty,
        kakousijiGroupDialogState,
        nextKakousijiDetailRowId,
        normalizeKakousijiGroupNo,
        normalizeKakousijiText,
        readKakousijiRowValueIgnoreCase,
        resolveDetailRowValue,
        resolveKakousijiGridPanelByType,
        resolveKakousijiGroupItemContext,
        resolveKakousijiSourceFieldName,
        setKakousijiGridRows,
        setKakousijiGroupControlState,
        setKakousijiQualityGroupControlState,
        setKakousijiGroupDialogState,
        syncKakousijiGridRowsFromApi
    } = deps;
    // グループ追加/編集後の選択更新はスコープ別に行う(品質表示は副資材と独立)
    function setScopeGroupControlState(panelType, itemCd, groupNo) {
        if (panelType === KAKOUSIJI_QUALITY_BASE_PANEL_TYPE) {
            setKakousijiQualityGroupControlState((prev) => ({ ...prev, itemCd: itemCd, groupNo: groupNo }));
            return;
        }
        setKakousijiGroupControlState((prev) => ({ ...prev, itemCd: itemCd, groupNo: groupNo }));
    }

    function resolveKakousijiGroupFieldId(panelType) {
        if (panelType === KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE) return 'SSDZ_FUKUSIZAICOLORGRP';
        if (panelType === KAKOUSIJI_QUALITY_BASE_PANEL_TYPE) return 'SSDQ_GROUPNO';
        return '';
    }
    function resolveKakousijiColorFieldId(panelType) {
        if (panelType === KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE) return 'SSDZ_COLORCD';
        if (panelType === KAKOUSIJI_QUALITY_BASE_PANEL_TYPE) return 'SSDQ_COLORCD';
        return '';
    }
    function resolveKakousijiColorOptionsForDialog(panelType, itemCd, groupNo) {
        const targetItem = normalizeKakousijiText(itemCd);
        const targetGroup = normalizeKakousijiGroupNo(groupNo);
        const colorFieldId = resolveKakousijiColorFieldId(panelType);
        const groupFieldId = resolveKakousijiGroupFieldId(panelType);
        if (targetItem === '' || colorFieldId === '' || groupFieldId === '') return [];
        const map = {};
        const pdtcRows = Array.isArray(initialPdtcDetail220Ref.current) ? initialPdtcDetail220Ref.current : [];
        pdtcRows.forEach((row) => {
            const colorCd = normalizeKakousijiText(resolveDetailRowValue(row, 'PDTC_COLORCD', ''));
            if (colorCd === '') return;
            const colorName = normalizeKakousijiText(resolveDetailRowValue(row, 'PDTC_COLORNM', ''));
            if (isEmpty(map[colorCd])) {
                map[colorCd] = { colorCd, colorName, checked: false };
                return;
            }
            if (normalizeKakousijiText(map[colorCd].colorName) === '' && colorName !== '') map[colorCd].colorName = colorName;
        });
        getKakousijiGridRowsByType(panelType).forEach((row) => {
            const context = resolveKakousijiGroupItemContext(row);
            if (context.itemCd !== targetItem) return;
            const colorCd = normalizeKakousijiText(readKakousijiRowValueIgnoreCase(row, colorFieldId));
            if (colorCd === '') return;
            const groupValue = normalizeKakousijiGroupNo(readKakousijiRowValueIgnoreCase(row, groupFieldId));
            const colorName = resolveKakousijiSourceFieldName(row, colorFieldId, colorCd);
            if (isEmpty(map[colorCd])) {
                map[colorCd] = { colorCd, colorName, checked: groupValue === targetGroup };
                return;
            }
            if (normalizeKakousijiText(map[colorCd].colorName) === '' && colorName !== '') map[colorCd].colorName = colorName;
            if (groupValue === targetGroup) map[colorCd].checked = true;
        });
        return Object.keys(map).sort((a, b) => a.localeCompare(b, 'ja', { numeric: true, sensitivity: 'base' })).map((key) => map[key]);
    }
    function openKakousijiGroupDialog(panelType, mode, itemCd, groupNo) {
        const targetItem = normalizeKakousijiText(itemCd);
        const targetGroup = normalizeKakousijiGroupNo(groupNo);
        if (targetItem === '' || targetGroup === '') return;
        setKakousijiGroupDialogState({
            open: true,
            mode,
            panelType,
            title: mode === 'delete' ? 'Care Label Delete' : mode === 'add' ? 'Group Add' : 'Group Edit',
            itemCd: targetItem,
            groupNo: targetGroup,
            colorOptions: mode === 'delete' ? [] : resolveKakousijiColorOptionsForDialog(panelType, targetItem, targetGroup)
        });
    }
    function closeKakousijiGroupDialog() {
        setKakousijiGroupDialogState({ open: false, mode: '', panelType: '', title: '', itemCd: '', groupNo: '', colorOptions: [] });
    }
    function updateKakousijiGroupDialogColorChecked(colorCd, checked) {
        const target = normalizeKakousijiText(colorCd);
        setKakousijiGroupDialogState((prev) => ({
            ...prev,
            colorOptions: (Array.isArray(prev.colorOptions) ? prev.colorOptions : []).map((item) =>
                normalizeKakousijiText(DicVal(item, 'colorCd', '')) === target ? { ...item, checked: !!checked } : item
            )
        }));
    }
    function applyKakousijiGroupDelete(panelType, itemCd, groupNo) {
        const panelInfo = resolveKakousijiGridPanelByType(panelType);
        const targetItem = normalizeKakousijiText(itemCd);
        const targetGroup = normalizeKakousijiGroupNo(groupNo);
        const groupFieldId = resolveKakousijiGroupFieldId(panelType);
        if (isEmpty(panelInfo) || targetItem === '' || targetGroup === '' || groupFieldId === '') return false;
        const currentRows = DeepCopy(syncKakousijiGridRowsFromApi(panelInfo));
        const nextRows = currentRows.filter((row) => {
            const context = resolveKakousijiGroupItemContext(row);
            const rowGroup = normalizeKakousijiGroupNo(readKakousijiRowValueIgnoreCase(row, groupFieldId));
            return !(context.itemCd === targetItem && rowGroup === targetGroup);
        });
        if (nextRows.length === currentRows.length) return false;
        setKakousijiGridRows(panelInfo, nextRows);
        const nextGroups = getKnownKakousijiGroupNos(targetItem);
        setKakousijiGroupControlState((prev) => ({ ...prev, itemCd: targetItem, groupNo: prev.itemCd === targetItem ? normalizeKakousijiGroupNo(DicVal(nextGroups, 0, '')) : prev.groupNo }));
        return true;
    }
    function applyKakousijiGroupAdd(panelType, itemCd, groupNo, colorOptions) {
        const panelInfo = resolveKakousijiGridPanelByType(panelType);
        const targetItem = normalizeKakousijiText(itemCd);
        const targetGroup = normalizeKakousijiGroupNo(groupNo);
        const colorFieldId = resolveKakousijiColorFieldId(panelType);
        if (isEmpty(panelInfo) || targetItem === '' || targetGroup === '') return false;
        if (getKnownKakousijiGroupNos(targetItem).includes(targetGroup)) return false;
        const checkedColors = (Array.isArray(colorOptions) ? colorOptions : [])
            .filter((item) => !!DicVal(item, 'checked', false))
            .map((item) => normalizeKakousijiText(DicVal(item, 'colorCd', '')))
            .filter((value, index, list) => value !== '' && list.indexOf(value) === index);
        const rows = DeepCopy(syncKakousijiGridRowsFromApi(panelInfo));
        let nextRowId = nextKakousijiDetailRowId(rows);
        if (checkedColors.length === 0) {
            const nextRow = createKakousijiGroupRow(panelType, targetItem, targetGroup);
            nextRow.rowid = String(nextRowId++);
            rows.push(nextRow);
        } else {
            checkedColors.forEach((colorCd) => {
                const nextRow = createKakousijiGroupRow(panelType, targetItem, targetGroup);
                if (panelType === KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE) {
                    nextRow.SSDZ_ITMCITEMCD = targetItem;
                    nextRow.SSDZ_FUKUSIZAICOLORGRP = targetGroup;
                    nextRow.SSDZ_COLORCD = colorCd;
                } else if (panelType === KAKOUSIJI_QUALITY_BASE_PANEL_TYPE) {
                    nextRow.SSDQ_ITMCITEMCD = targetItem;
                    nextRow.SSDQ_GROUPNO = targetGroup;
                    nextRow.SSDQ_FUKUSIZAICOLORGRP = targetGroup;
                    nextRow.SSDQ_COLORCD = colorCd;
                }
                nextRow.rowid = String(nextRowId++);
                rows.push(nextRow);
            });
        }
        setKakousijiGridRows(panelInfo, rows);
        setScopeGroupControlState(panelType, targetItem, targetGroup);
        return true;
    }
    function applyKakousijiGroupEdit(panelType, itemCd, groupNo, colorOptions) {
        const panelInfo = resolveKakousijiGridPanelByType(panelType);
        const targetItem = normalizeKakousijiText(itemCd);
        const targetGroup = normalizeKakousijiGroupNo(groupNo);
        const colorFieldId = resolveKakousijiColorFieldId(panelType);
        const groupFieldId = resolveKakousijiGroupFieldId(panelType);
        if (isEmpty(panelInfo) || targetItem === '' || targetGroup === '' || colorFieldId === '' || groupFieldId === '') return false;
        const checkedColors = new Set((Array.isArray(colorOptions) ? colorOptions : []).filter((item) => !!DicVal(item, 'checked', false)).map((item) => normalizeKakousijiText(DicVal(item, 'colorCd', ''))).filter((value) => value !== ''));
        const currentRows = DeepCopy(syncKakousijiGridRowsFromApi(panelInfo));
        const nextRows = [];
        let changed = false;
        currentRows.forEach((row) => {
            const nextRow = isEmpty(row) ? {} : { ...row };
            const context = resolveKakousijiGroupItemContext(nextRow);
            const rowColor = normalizeKakousijiText(readKakousijiRowValueIgnoreCase(nextRow, colorFieldId));
            const rowGroup = normalizeKakousijiGroupNo(readKakousijiRowValueIgnoreCase(nextRow, groupFieldId));
            if (context.itemCd !== targetItem || rowColor === '') {
                nextRows.push(nextRow);
                return;
            }
            if (checkedColors.has(rowColor)) {
                if (rowGroup !== targetGroup) {
                    nextRow[groupFieldId] = targetGroup;
                    changed = true;
                }
                nextRows.push(nextRow);
                return;
            }
            if (rowGroup === targetGroup) {
                changed = true;
                return;
            }
            nextRows.push(nextRow);
        });
        if (!changed) return false;
        setKakousijiGridRows(panelInfo, nextRows);
        setScopeGroupControlState(panelType, targetItem, targetGroup);
        return true;
    }
    function handleKakousijiGroupCopy(panelType, targetItemCd, targetGroupNo, sourceItemCd, sourceGroupNo) {
        const targetType = normalizeKakousijiText(panelType);
        const targetItem = normalizeKakousijiText(targetItemCd);
        const targetGroup = normalizeKakousijiGroupNo(targetGroupNo);
        const sourceItem = normalizeKakousijiText(sourceItemCd);
        const sourceGroup = normalizeKakousijiGroupNo(sourceGroupNo);
        const groupFieldId = resolveKakousijiGroupFieldId(targetType);
        const panelInfo = resolveKakousijiGridPanelByType(targetType);
        if (isEmpty(panelInfo) || targetItem === '' || targetGroup === '' || sourceItem === '' || sourceGroup === '' || groupFieldId === '') return false;
        const currentRows = DeepCopy(syncKakousijiGridRowsFromApi(panelInfo));
        const sourceRows = currentRows.filter((row) => {
            const context = resolveKakousijiGroupItemContext(row);
            const rowGroup = normalizeKakousijiGroupNo(readKakousijiRowValueIgnoreCase(row, groupFieldId));
            return context.itemCd === sourceItem && rowGroup === sourceGroup;
        });
        if (sourceRows.length === 0) {
            fnc.current.warnmsg('複写元グループにデータがありません');
            return false;
        }
        const nextRows = currentRows.filter((row) => {
            const context = resolveKakousijiGroupItemContext(row);
            const rowGroup = normalizeKakousijiGroupNo(readKakousijiRowValueIgnoreCase(row, groupFieldId));
            return !(context.itemCd === targetItem && rowGroup === targetGroup);
        });
        let nextRowId = nextKakousijiDetailRowId(nextRows);
        sourceRows.forEach((row) => {
            const copied = isEmpty(row) ? {} : DeepCopy(row);
            copied.rowid = String(nextRowId++);
            if (targetType === KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE) {
                copied.SSDZ_ITMCITEMCD = targetItem;
                copied.SSDZ_FUKUSIZAICOLORGRP = targetGroup;
            } else if (targetType === KAKOUSIJI_QUALITY_BASE_PANEL_TYPE) {
                copied.SSDQ_ITMCITEMCD = targetItem;
                copied.SSDQ_GROUPNO = targetGroup;
                copied.SSDQ_FUKUSIZAICOLORGRP = targetGroup;
            }
            nextRows.push(copied);
        });
        setKakousijiGridRows(panelInfo, nextRows);
        setKakousijiGroupControlState((prev) => ({
            ...prev,
            itemCd: targetItem,
            groupNo: targetGroup,
            sourceItemCd: sourceItem,
            sourceGroupNo: sourceGroup
        }));
        return true;
    }
    function commitKakousijiGroupDialog() {
        const panelType = normalizeKakousijiText(kakousijiGroupDialogState.panelType);
        const itemCd = normalizeKakousijiText(kakousijiGroupDialogState.itemCd);
        const groupNo = normalizeKakousijiGroupNo(kakousijiGroupDialogState.groupNo);
        if (panelType === '' || itemCd === '' || groupNo === '') {
            closeKakousijiGroupDialog();
            return;
        }
        if (kakousijiGroupDialogState.mode === 'delete') {
            applyKakousijiGroupDelete(panelType, itemCd, groupNo);
            closeKakousijiGroupDialog();
            return;
        }
        if (kakousijiGroupDialogState.mode === 'add') {
            if (getKnownKakousijiGroupNos(itemCd).includes(groupNo)) {
                fnc.current.warnmsg('The specified group already exists.');
                return;
            }
            applyKakousijiGroupAdd(panelType, itemCd, groupNo, kakousijiGroupDialogState.colorOptions);
            closeKakousijiGroupDialog();
            return;
        }
        const checkedCount = (Array.isArray(kakousijiGroupDialogState.colorOptions) ? kakousijiGroupDialogState.colorOptions : []).filter((item) => !!DicVal(item, 'checked', false)).length;
        if (checkedCount === 0) {
            fnc.current.warnmsg('Select at least one color.');
            return;
        }
        applyKakousijiGroupEdit(panelType, itemCd, groupNo, kakousijiGroupDialogState.colorOptions);
        closeKakousijiGroupDialog();
    }

    return {
        resolveKakousijiGroupFieldId,
        resolveKakousijiColorFieldId,
        resolveKakousijiColorOptionsForDialog,
        openKakousijiGroupDialog,
        closeKakousijiGroupDialog,
        updateKakousijiGroupDialogColorChecked,
        applyKakousijiGroupDelete,
        applyKakousijiGroupAdd,
        applyKakousijiGroupEdit,
        handleKakousijiGroupCopy,
        commitKakousijiGroupDialog
    };
}
